import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth.tsx';
import { supabase } from '../lib/db.ts';
import { Building2, Globe, FileText, Lightbulb, CheckCircle, AlertCircle, Copy, Eye, EyeOff } from 'lucide-react';
import { logger } from '../services/logger.ts';
import { toast } from 'react-hot-toast';

export function DeveloperRegistration() {
  const { user, loading: authLoading } = useAuth();
  const [step, setStep] = useState<'register' | 'generate-key' | 'success'>('register');
  const [formData, setFormData] = useState({
    company_name: '',
    website: '',
    description: '',
    use_case: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [accountStatus, setAccountStatus] = useState<'pending' | 'approved' | 'rejected' | null>(null);
  const [apiKeyData, setApiKeyData] = useState<{
    api_key: string;
    signing_secret: string;
    api_key_id: string;
    environment: string;
  } | null>(null);
  const [showSecrets, setShowSecrets] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [existingApiKeys, setExistingApiKeys] = useState<Array<{
    id: string;
    key_name: string;
    environment: string;
    is_active: boolean;
    created_at: string;
    last_used_at: string | null;
  }>>([]);
  const [loadingKeys, setLoadingKeys] = useState(false);

  // Note: We allow users to view the form even if not logged in
  // They'll need to log in or register before submitting

  // Check existing developer account
  useEffect(() => {
    if (user) {
      checkDeveloperAccount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const checkDeveloperAccount = async () => {
    try {
      const { data } = await supabase
        .from('developer_accounts')
        .select('status')
        .eq('user_id', user?.id)
        .single();

      if (data) {
        setAccountStatus(data.status);
        if (data.status === 'approved') {
          // Fetch existing API keys
          fetchExistingApiKeys();
        }
      }
    } catch {
      // Account doesn't exist yet, that's fine
    }
  };

  const fetchExistingApiKeys = async () => {
    try {
      setLoadingKeys(true);
      const { data, error } = await supabase.rpc('get_developer_api_keys_with_stats');

      if (error) throw error;

      if (data && data.success) {
        setExistingApiKeys(data.api_keys || []);
      }
    } catch (err) {
      console.error('Error fetching API keys:', err);
      // Don't show error toast here, just log it
    } finally {
      setLoadingKeys(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Company or project name is required';
    }

    if (formData.website && !formData.website.match(/^https?:\/\/.+/)) {
      newErrors.website = 'Please enter a valid URL (e.g., https://example.com)';
    }

    if (!formData.description.trim() || formData.description.trim().length < 20) {
      newErrors.description = 'Please provide a description (at least 20 characters)';
    }

    if (!formData.use_case.trim()) {
      newErrors.use_case = 'Please describe your use case';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if user is logged in
    if (!user) {
      setErrors({ submit: 'Please log in or create an account to register as a developer.' });
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // Call database function directly
      const { data: result, error: rpcError } = await supabase.rpc('register_developer', {
        p_company_name: formData.company_name,
        p_website: formData.website || null,
        p_description: formData.description,
        p_use_case: formData.use_case
      });

      if (rpcError || !result?.success) {
        throw new Error(rpcError?.message || result?.error || 'Registration failed');
      }

      setAccountStatus('pending');
      logger.info('Developer account registration submitted', {
        userId: user?.id,
        companyName: formData.company_name,
        developerAccountId: result.developer_account_id
      });

      // Show success message
      toast.success('Registration submitted successfully! Your account will be reviewed by our team. You will receive an email once approved.', {
        duration: 6000,
        icon: '✅'
      });

    } catch (error) {
      logger.error('Developer registration error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Registration failed. Please try again.';
      setErrors({ submit: errorMessage });
      toast.error(errorMessage, {
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAPIKey = async (environment: 'test' | 'production' = 'test') => {
    if (!user) return;

    setLoading(true);
    setErrors({});

    try {
      const keyName = `${formData.company_name || 'My App'} - ${environment === 'test' ? 'Test' : 'Production'}`;

      // Call database function directly
      const { data: result, error: rpcError } = await supabase.rpc('generate_developer_api_key', {
        p_key_name: keyName,
        p_environment: environment,
        p_expires_at: null,
        p_metadata: {
          generated_via: 'developer_portal',
          company_name: formData.company_name
        }
      });

      if (rpcError || !result?.success) {
        throw new Error(rpcError?.message || result?.error || 'Failed to generate API key');
      }

      setApiKeyData({
        api_key: result.api_key,
        signing_secret: result.signing_secret,
        api_key_id: result.api_key_id,
        environment: result.environment
      });

      // Encrypt and store the signing secret
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const encryptResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/encrypt-signing-secret`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify({
              api_key_id: result.api_key_id,
              signing_secret: result.signing_secret
            })
          });

          const encryptResult = await encryptResponse.json();
          if (!encryptResponse.ok || !encryptResult.success) {
            console.warn('Failed to encrypt signing secret:', encryptResult.error);
            // Don't fail the whole process, but log the warning
          }
        }
      } catch (encryptError) {
        console.error('Error encrypting signing secret:', encryptError);
        // Don't fail the whole process
      }

      setStep('success');
      logger.info('API key generated', {
        userId: user?.id,
        environment: result.environment,
        keyId: result.api_key_id
      });

    } catch (error) {
      logger.error('API key generation error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate API key. Please try again.';
      setErrors({ submit: errorMessage });
      toast.error(errorMessage, {
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string, id: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-wayja-cream flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-wayja-teal mx-auto"></div>
          <p className="mt-4 text-wayja-dark-slate">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-wayja-cream">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-wayja-black mb-4">
            Developer Account Registration
          </h1>
          <p className="text-xl text-wayja-dark-slate">
            Register to get API access and start building with Wayja
          </p>
        </div>

        {/* Registration Form */}
        {step === 'register' && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            {!user && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-blue-800">Account Required</h4>
                    <p className="text-blue-700 text-sm mt-1">
                      You need to be logged in to register as a developer.{' '}
                      <Link to="/auth/login?redirect=/developers/register" className="underline font-medium">
                        Log in
                      </Link>
                      {' '}or{' '}
                      <Link to="/auth/register?redirect=/developers/register" className="underline font-medium">
                        create an account
                      </Link>
                      {' '}to continue.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {accountStatus === 'pending' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-blue-800">Account Pending Approval</h4>
                    <p className="text-blue-700 text-sm mt-1">
                      Your developer account registration is pending review. You will receive an email once your account is approved.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {accountStatus === 'rejected' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-red-800">Account Rejected</h4>
                    <p className="text-red-700 text-sm mt-1">
                      Your developer account registration was rejected. Please contact{' '}
                      <a href="mailto:support@wayja.me" className="underline">support@wayja.me</a> for more information.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {accountStatus === 'approved' && (
              <>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-green-800">Account Approved</h4>
                      <p className="text-green-700 text-sm mt-1">
                        Your developer account has been approved! {existingApiKeys.length > 0 ? 'Here are your existing API keys:' : 'You can generate new API keys below.'}
                      </p>
                    </div>
                  </div>
                </div>

                {loadingKeys ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-wayja-teal mx-auto"></div>
                      <p className="mt-2 text-sm text-gray-600">Loading API keys...</p>
                    </div>
                  </div>
                ) : existingApiKeys.length > 0 ? (
                  <div className="bg-white border-2 border-green-200 rounded-lg p-6 mb-6">
                    <h3 className="text-lg font-semibold text-wayja-black mb-4">Your API Keys</h3>
                    <div className="space-y-4">
                      {existingApiKeys.map((key) => (
                        <div key={key.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-medium text-wayja-black">{key.key_name}</h4>
                              <div className="flex items-center gap-3 mt-1">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  key.environment === 'test' 
                                    ? 'bg-blue-100 text-blue-800' 
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {key.environment === 'test' ? 'Test' : 'Production'}
                                </span>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  key.is_active 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {key.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </div>
                            <Link
                              to="/developers/dashboard"
                              className="text-wayja-teal hover:text-wayja-dark-teal text-sm font-medium"
                            >
                              View Details →
                            </Link>
                          </div>
                          <div className="mt-2 text-xs text-gray-600">
                            <p>Created: {new Date(key.created_at).toLocaleDateString()}</p>
                            {key.last_used_at && (
                              <p>Last used: {new Date(key.last_used_at).toLocaleDateString()}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <Link
                        to="/developers/dashboard"
                        className="inline-flex items-center text-wayja-teal hover:text-wayja-dark-teal font-medium"
                      >
                        Manage all API keys in Dashboard →
                      </Link>
                    </div>
                  </div>
                ) : null}
              </>
            )}

            {/* Only show registration form if account is not approved */}
            {accountStatus !== 'approved' && (
              <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="company_name" className="block text-sm font-medium text-wayja-black mb-2">
                  <Building2 className="w-4 h-4 inline mr-2" />
                  Company or Project Name *
                </label>
                <input
                  type="text"
                  id="company_name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-wayja-teal focus:border-transparent ${
                    errors.company_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="My Awesome App"
                  disabled={accountStatus === 'pending'}
                />
                {errors.company_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.company_name}</p>
                )}
              </div>

              <div>
                <label htmlFor="website" className="block text-sm font-medium text-wayja-black mb-2">
                  <Globe className="w-4 h-4 inline mr-2" />
                  Website (Optional)
                </label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-wayja-teal focus:border-transparent ${
                    errors.website ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="https://example.com"
                  disabled={!user || accountStatus === 'pending'}
                />
                {errors.website && (
                  <p className="mt-1 text-sm text-red-600">{errors.website}</p>
                )}
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-wayja-black mb-2">
                  <FileText className="w-4 h-4 inline mr-2" />
                  Description *
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-wayja-teal focus:border-transparent ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Describe your project and how you plan to use Wayja's API..."
                  disabled={!user || accountStatus === 'pending'}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                )}
              </div>

              <div>
                <label htmlFor="use_case" className="block text-sm font-medium text-wayja-black mb-2">
                  <Lightbulb className="w-4 h-4 inline mr-2" />
                  Use Case *
                </label>
                <textarea
                  id="use_case"
                  name="use_case"
                  value={formData.use_case}
                  onChange={handleChange}
                  rows={3}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-wayja-teal focus:border-transparent ${
                    errors.use_case ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="What will you build with Wayja? (e.g., Trading bot, News widget, Analytics dashboard)"
                  disabled={!user || accountStatus === 'pending'}
                />
                {errors.use_case && (
                  <p className="mt-1 text-sm text-red-600">{errors.use_case}</p>
                )}
              </div>

              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800 text-sm">{errors.submit}</p>
                </div>
              )}

              {accountStatus !== 'pending' && (
                <button
                  type="submit"
                  disabled={loading || !user}
                  className="w-full bg-wayja-teal hover:bg-wayja-dark-teal text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Submitting...' : !user ? 'Please Log In First' : 'Submit Registration'}
                </button>
              )}
            </form>
            )}

            {/* Action buttons for approved accounts */}
            {accountStatus === 'approved' && (
              <div className="flex gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => setStep('generate-key')}
                  className="flex-1 bg-wayja-teal hover:bg-wayja-dark-teal text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Generate New API Key
                </button>
                <Link
                  to="/developers/dashboard"
                  className="flex-1 border-2 border-wayja-teal text-wayja-teal hover:bg-wayja-teal hover:text-white px-6 py-3 rounded-lg font-semibold text-center transition-colors"
                >
                  Go to Dashboard
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Generate API Key */}
        {step === 'generate-key' && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-wayja-black mb-6">Generate API Key</h2>
            
            <div className="space-y-4 mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">Test Environment</h4>
                <p className="text-blue-700 text-sm mb-3">
                  Start with a test API key to develop and test your integration safely. Test keys have access to:
                </p>
                <ul className="list-disc list-inside text-blue-700 text-sm space-y-1">
                  <li>Test markets and data</li>
                  <li>Sandbox payment processing</li>
                  <li>No real money transactions</li>
                  <li>Full API functionality</li>
                </ul>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-medium text-yellow-800 mb-2">Production Environment</h4>
                <p className="text-yellow-700 text-sm">
                  Production keys require additional approval and have access to live markets and real transactions.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => handleGenerateAPIKey('test')}
                disabled={loading}
                className="flex-1 bg-wayja-teal hover:bg-wayja-dark-teal text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50"
              >
                {loading ? 'Generating...' : 'Generate Test API Key'}
              </button>
              <button
                onClick={() => handleGenerateAPIKey('production')}
                disabled={loading}
                className="flex-1 bg-wayja-coral hover:bg-wayja-dark-coral text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50"
              >
                {loading ? 'Generating...' : 'Request Production Key'}
              </button>
            </div>

            {errors.submit && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm">{errors.submit}</p>
              </div>
            )}

            <div className="mt-6">
              <Link
                to="/developers"
                className="text-wayja-teal hover:text-wayja-dark-teal font-medium"
              >
                ← Back to Developer Portal
              </Link>
            </div>
          </div>
        )}

        {/* Success - Show API Key */}
        {step === 'success' && apiKeyData && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-center mb-6">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-wayja-black mb-2">API Key Generated Successfully!</h2>
              <p className="text-wayja-dark-slate">
                Save your credentials now - the signing secret will not be shown again.
              </p>
            </div>

            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-red-800 mb-1">Important Security Notice</h4>
                  <p className="text-red-700 text-sm">
                    Your signing secret will only be displayed once. Make sure to copy and store it securely. 
                    You will need it for HMAC request signing.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-wayja-black mb-2">
                  API Key
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm break-all">
                    {apiKeyData.api_key}
                  </div>
                  <button
                    onClick={() => copyToClipboard(apiKeyData.api_key, 'api-key')}
                    className="p-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
                  >
                    {copied === 'api-key' ? <CheckCircle className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-wayja-black mb-2">
                  Signing Secret
                  <button
                    onClick={() => setShowSecrets(!showSecrets)}
                    className="ml-2 text-wayja-teal hover:text-wayja-dark-teal"
                  >
                    {showSecrets ? <EyeOff className="w-4 h-4 inline" /> : <Eye className="w-4 h-4 inline" />}
                  </button>
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm break-all">
                    {showSecrets ? apiKeyData.signing_secret : '•'.repeat(64)}
                  </div>
                  <button
                    onClick={() => copyToClipboard(apiKeyData.signing_secret, 'signing-secret')}
                    className="p-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
                  >
                    {copied === 'signing-secret' ? <CheckCircle className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">
                  <strong>Environment:</strong> {apiKeyData.environment === 'test' ? 'Test' : 'Production'}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Base URL:</strong> {
                    apiKeyData.environment === 'test' && import.meta.env.VITE_TEST_API_BASE_URL
                      ? import.meta.env.VITE_TEST_API_BASE_URL
                      : `${import.meta.env.VITE_SUPABASE_URL || 'https://uyanvkwkikftzpanolmw.supabase.co'}/functions/v1`
                  }
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <Link
                to="/developers/dashboard"
                className="flex-1 bg-wayja-teal hover:bg-wayja-dark-teal text-white px-6 py-3 rounded-lg font-semibold text-center transition-colors"
              >
                Go to Dashboard
              </Link>
              <Link
                to="/developers/quickstart"
                className="flex-1 border-2 border-wayja-teal text-wayja-teal hover:bg-wayja-teal hover:text-white px-6 py-3 rounded-lg font-semibold text-center transition-colors"
              >
                Quick Start Guide
              </Link>
            </div>
          </div>
        )}

        {/* Help Section */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-wayja-black mb-4">Need Help?</h3>
          <div className="space-y-2 text-sm text-wayja-dark-slate">
            <p>
              <strong>Questions about registration?</strong> Contact us at{' '}
              <a href="mailto:support@wayja.me" className="text-wayja-teal hover:text-wayja-dark-teal">
                support@wayja.me
              </a>
            </p>
            <p>
              <strong>Technical support?</strong> Check out our{' '}
              <Link to="/developers/api-reference" className="text-wayja-teal hover:text-wayja-dark-teal">
                API documentation
              </Link>
              {' '}or{' '}
              <Link to="/developers/examples" className="text-wayja-teal hover:text-wayja-dark-teal">
                code examples
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

